import { useEffect, useState } from "react";
import { Link, useParams, useHistory } from "react-router-dom";
import axios from "axios";
import { Printer } from "react-bootstrap-icons";
import { LoadingBlock, ErrorBlock } from "../../common/ui/StateBlock";
import { carTitle, formatDate, display } from "../../common/format";
import { SELLER, geoWords, gelNum } from "./invoiceShared";
import "./Auction.css";
import "../../common/ui/ui.css";

// Printable invoice for a won car (auction settlement), styled on the official
// "შპს ქარ ექსპრესი" invoice template — the same layout as the deposit invoice.

const STATUS_GEO = { unpaid: "გადაუხდელი", paid: "გადახდილი", cancelled: "გაუქმებული", overdue: "ვადაგადაცილებული" };

const AuctionInvoice = () => {
    const { id } = useParams();
    const history = useHistory();
    const [inv, setInv] = useState(null);
    const [state, setState] = useState("loading");

    useEffect(() => {
        let alive = true;
        axios.get(`/api/auction/my/invoices/${id}`)
            .then((res) => { if (alive) { setInv(res.data.invoice); setState("ready"); } })
            .catch((err) => {
                if (!alive) return;
                if (err && err.response && err.response.status === 401) { history.replace("/auction/login"); return; }
                setState("error");
            });
        return () => { alive = false; };
    }, [id, history]);

    if (state === "loading") return <div className="container auction-narrow"><LoadingBlock label="იტვირთება..." /></div>;
    if (state === "error" || !inv) return <div className="container auction-narrow"><ErrorBlock title="ვერ მოიძებნა" message="ინვოისი ვერ მოიძებნა." /></div>;

    const isCompany = inv.account_type === "company";
    const buyerName = isCompany ? display(inv.company_name) : `${inv.name || ""} ${inv.surname || ""}`.trim();
    const buyerId = isCompany ? inv.company_id : inv.personal_id;

    const created = inv.created_at ? new Date(inv.created_at) : new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const invoiceNo = `CX-${created.getFullYear()}${pad(created.getMonth() + 1)}${pad(created.getDate())}-${inv.id}`;
    const premium = Number(inv.buyers_premium) || 0;
    const total = inv.total != null ? Number(inv.total) : (Number(inv.amount) || 0) + premium;

    return (
        <div className="container auction-invoice-page">
            <div className="auction-invoice-toolbar no-print">
                <Link to="/auction/my" className="auction-back">← ჩემი აუქციონები</Link>
                <button className="cx-btn cx-btn-primary" onClick={() => window.print()}><Printer /> ჩამოტვირთვა (PDF)</button>
            </div>

            <div className="cx-card auction-invoice-doc auction-dinv">
                {/* Top meta */}
                <div className="auction-dinv-top">
                    <div className="auction-dinv-brand">
                        <span className="auction-dinv-logo"><img src="/static/icons/logo.png" alt="CAR EXPRESS" /></span>
                        <div className="auction-muted auction-dinv-tagline">ონლაინ აუქციონი</div>
                    </div>
                    <dl className="auction-dinv-meta">
                        <div><dt>ინვოისი №</dt><dd>{invoiceNo}</dd></div>
                        <div><dt>თარიღი</dt><dd>{formatDate(created)}</dd></div>
                        <div><dt>გადახდის ვადა</dt><dd>{inv.due_at ? formatDate(inv.due_at) : "—"}</dd></div>
                        <div><dt>ვალუტა</dt><dd>ლარი (GEL)</dd></div>
                        <div><dt>სტატუსი</dt><dd>{STATUS_GEO[inv.status] || inv.status}</dd></div>
                    </dl>
                </div>

                <h1 className="auction-dinv-title">ინვოისი / ანგარიშ-ფაქტურა</h1>

                {/* Parties */}
                <div className="auction-dinv-parties">
                    <div className="auction-dinv-party">
                        <h4>გამყიდველი</h4>
                        <div className="auction-dinv-row"><span>დასახელება</span><strong>{SELLER.name}</strong></div>
                        <div className="auction-dinv-row"><span>საიდ. კოდი</span><span>{SELLER.idCode}</span></div>
                        <div className="auction-dinv-row"><span>ბანკი</span><span>{SELLER.bank}</span></div>
                        <div className="auction-dinv-row"><span>IBAN</span><span>{SELLER.iban}</span></div>
                    </div>
                    <div className="auction-dinv-party">
                        <h4>მყიდველი</h4>
                        <div className="auction-dinv-row"><span>{isCompany ? "დასახელება" : "სახელი, გვარი"}</span><strong>{buyerName || "—"}</strong></div>
                        <div className="auction-dinv-row"><span>{isCompany ? "საიდ. №" : "პირადი №"}</span><span>{display(buyerId)}</span></div>
                        <div className="auction-dinv-row"><span>მისამართი</span><span>{display(inv.city)}</span></div>
                        <div className="auction-dinv-row"><span>ტელეფონი</span><span>{display(inv.phone)}</span></div>
                    </div>
                </div>

                {/* Services */}
                <div className="auction-dinv-sectitle">მომსახურება</div>
                <table className="auction-invoice-table auction-dinv-table">
                    <thead>
                        <tr>
                            <th>№</th>
                            <th>დასახელება</th>
                            <th>VIN</th>
                            <th className="ar">თანხა (GEL)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>1</td>
                            <td>
                                {carTitle(inv)} — მოგებული ლოტი
                                <div className="auction-muted auction-dinv-subrow">
                                    აუქციონი: {display(inv.event_title) || `#${inv.event_id}`} · ლოტი #{inv.lot_id}
                                </div>
                            </td>
                            <td>{display(inv.vin)}</td>
                            <td className="ar">{gelNum(inv.amount)}</td>
                        </tr>
                        {premium > 0 && (
                            <tr>
                                <td>2</td>
                                <td>მომსახურების საკომისიო</td>
                                <td>—</td>
                                <td className="ar">{gelNum(premium)}</td>
                            </tr>
                        )}
                    </tbody>
                </table>

                <div className="auction-dinv-total"><span>სულ:</span><strong>{gelNum(total)} ₾</strong></div>
                <p className="auction-dinv-words">თანხა სიტყვიერად: <strong>{geoWords(total)} ლარი</strong></p>

                {/* Payment instruction */}
                <div className="auction-dinv-instr">
                    <h4>გადახდის ინსტრუქცია</h4>
                    <p>
                        თანხა ჩაირიცხოს {SELLER.name}-ის ანგარიშზე <strong>{SELLER.iban}</strong> ({SELLER.bank}).
                        გადახდის დანიშნულებაში მიუთითეთ ინვოისის ნომერი <strong>{invoiceNo}</strong> და მოგებული ავტომობილი.
                    </p>
                    <p className="auction-muted">გთხოვთ, გადაიხადოთ მითითებულ ვადაში. კითხვების შემთხვევაში: info@carexpress.ge</p>
                </div>

                {/* Signatures */}
                <div className="auction-dinv-sign">
                    <div>
                        <div className="auction-dinv-row"><span>გამცემი</span><span>{SELLER.name}</span></div>
                        <div className="auction-dinv-sline">ხელმოწერა: ____________________</div>
                        <div className="auction-muted auction-dinv-ba">ბ.ა.</div>
                    </div>
                    <div>
                        <div className="auction-dinv-row"><span>მიმღები</span><span>{buyerName || "—"}</span></div>
                        <div className="auction-dinv-sline">ხელმოწერა: ____________________</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AuctionInvoice;
